/**
 * Pure, deterministic helpers for the KAN-224 Leak Sites redesign.
 *
 * Everything here is unit-tested in `leak-site-utils.test.ts` and free of
 * React / DOM / network so the page + components can stay declarative. Time-
 * dependent helpers accept an explicit `now` for testability.
 *
 * Honesty rule: these helpers only ever derive from data the API genuinely
 * returns (claims, the groups list, stats, the dashboard summary). They never
 * fabricate per-group week-over-week deltas, mirror/source health, confidence
 * scores, or onion URLs — those are not backed by the current contract.
 */

import type { GroupListItem, Layer2ClaimDto } from './types'

// ---------------------------------------------------------------------------
// Time windows
// ---------------------------------------------------------------------------

export type TimeWindow = '24h' | '7d' | '30d' | '90d'

export const TIME_WINDOWS: ReadonlyArray<{ value: TimeWindow; label: string }> = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
]

export const WINDOW_HOURS: Record<TimeWindow, number> = {
  '24h': 24,
  '7d': 168,
  '30d': 720,
  '90d': 2160,
}

/**
 * Convert a time window into an ISO `from`/`to` range ending at `now`, ready
 * to pass as `/claims` and `/stats` query params.
 */
export function timeWindowToRange(
  window: TimeWindow,
  now: number = Date.now(),
): { from: string; to: string } {
  const fromMs = now - WINDOW_HOURS[window] * 3_600_000
  return {
    from: new Date(fromMs).toISOString(),
    to: new Date(now).toISOString(),
  }
}

// ---------------------------------------------------------------------------
// Freshness (derived from the real claim timestamp)
// ---------------------------------------------------------------------------

export type FreshnessKey = 'live' | 'recent' | 'this-week' | 'aged'

const FRESHNESS_LABELS: Record<FreshnessKey, string> = {
  live: 'live',
  recent: 'recent',
  'this-week': 'this week',
  aged: 'aged',
}

/**
 * Classify a claim by age. `live` ≤24h, `recent` ≤72h, `this-week` ≤168h,
 * otherwise `aged`. A malformed date yields an explicit `undated` marker so
 * the UI never implies a freshness it cannot stand behind.
 */
export function freshnessOf(
  claimDateIso: string,
  now: number = Date.now(),
): { key: FreshnessKey; label: string } {
  const ms = Date.parse(claimDateIso)
  if (Number.isNaN(ms)) return { key: 'aged', label: 'undated' }
  const ageHours = (now - ms) / 3_600_000
  if (ageHours <= 24) return { key: 'live', label: FRESHNESS_LABELS.live }
  if (ageHours <= 72) return { key: 'recent', label: FRESHNESS_LABELS.recent }
  if (ageHours <= 168)
    return { key: 'this-week', label: FRESHNESS_LABELS['this-week'] }
  return { key: 'aged', label: FRESHNESS_LABELS.aged }
}

// ---------------------------------------------------------------------------
// Group-derived aggregates (from /groups recentClaimCount, a real 90d count)
// ---------------------------------------------------------------------------

export interface TopMover {
  id: string
  name: string
  count: number
  /** count / max(count) in the ranked set — a 0..1 bar width. */
  share: number
  threatActorId: string | null
  active: boolean
}

/**
 * Rank groups by their recent (90d) claim count. Groups with zero recent
 * claims are excluded; ties resolve by name for determinism. `share` is
 * relative to the busiest group so the UI can draw proportional bars.
 */
export function deriveTopMovers(
  groups: readonly GroupListItem[],
  limit = 5,
): TopMover[] {
  const ranked = groups
    .filter((g) => g.recentClaimCount > 0)
    .sort(
      (a, b) =>
        b.recentClaimCount - a.recentClaimCount || a.name.localeCompare(b.name),
    )
    .slice(0, limit)
  const max = ranked[0]?.recentClaimCount ?? 0
  return ranked.map((g) => ({
    id: g.id,
    name: g.name,
    count: g.recentClaimCount,
    share: max > 0 ? g.recentClaimCount / max : 0,
    threatActorId: g.threatActorId,
    active: g.active,
  }))
}

export interface GroupConcentration {
  totalRecent: number
  topShare: number
  topCount: number
  segments: Array<{ id: string; name: string; share: number }>
}

/**
 * Concentration of recent claim volume across the busiest `topN` groups —
 * "top 5 groups account for X% of this window's claims". All shares are of the
 * total recent claim count, never invented.
 */
export function deriveGroupConcentration(
  groups: readonly GroupListItem[],
  topN = 5,
): GroupConcentration {
  const active = groups.filter((g) => g.recentClaimCount > 0)
  const totalRecent = active.reduce((sum, g) => sum + g.recentClaimCount, 0)
  if (totalRecent === 0) {
    return { totalRecent: 0, topShare: 0, topCount: 0, segments: [] }
  }
  const ranked = [...active].sort(
    (a, b) =>
      b.recentClaimCount - a.recentClaimCount || a.name.localeCompare(b.name),
  )
  const top = ranked.slice(0, topN)
  const topCount = top.reduce((sum, g) => sum + g.recentClaimCount, 0)
  return {
    totalRecent,
    topCount,
    topShare: topCount / totalRecent,
    segments: top.map((g) => ({
      id: g.id,
      name: g.name,
      share: g.recentClaimCount / totalRecent,
    })),
  }
}

// ---------------------------------------------------------------------------
// Per-claim display helpers
// ---------------------------------------------------------------------------

/** Trimmed victim name, or a neutral fallback when the feed had no identifier. */
export function victimDisplayName(claim: { victimName: string | null }): string {
  const name = claim.victimName?.trim()
  return name && name.length > 0 ? name : 'Undisclosed victim'
}

/** Trimmed post text, truncated with an ellipsis past `maxLen`. Null when blank. */
export function postExcerpt(
  description: string | null,
  maxLen = 200,
): string | null {
  const text = description?.trim()
  if (!text) return null
  if (text.length <= maxLen) return text
  return `${text.slice(0, maxLen).trimEnd()}…`
}

/** Format an ISO timestamp as `YYYY-MM-DD HH:mm UTC`, or null when malformed. */
export function formatUtcTimestamp(iso: string): string | null {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return null
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  )
}

// ---------------------------------------------------------------------------
// Dossier view-model: claim joined with its ransomware group
// ---------------------------------------------------------------------------

export interface DossierClaim extends Layer2ClaimDto {
  groupName: string
  threatActorId: string | null
  groupActive: boolean
}

/**
 * Join a layer-2 claim against the loaded groups list to add the display
 * group name, threat-actor link target, and active flag. Degrades to
 * "Unknown group" / no link when the group is not in the map.
 */
export function enrichDossierClaim(
  claim: Layer2ClaimDto,
  groupsById: Map<string, GroupListItem>,
): DossierClaim {
  const group = groupsById.get(claim.groupId)
  return {
    ...claim,
    groupName: group?.name ?? 'Unknown group',
    threatActorId: group?.threatActorId ?? null,
    groupActive: group?.active ?? false,
  }
}

// ---------------------------------------------------------------------------
// Client-side refinement of the loaded page (search / sort / chips)
// ---------------------------------------------------------------------------

/**
 * Free-text search over the loaded dossier rows. Matches victim name, post
 * text, group name, source, sector, country, source URL and related CVEs.
 * Operates on the currently-loaded page only (server handles sector/country/
 * group/window + pagination); callers surface that scope honestly.
 */
export function searchClaims<T extends DossierClaim>(
  claims: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...claims]
  return claims.filter((c) => {
    const haystack = [
      c.victimName,
      c.description,
      c.groupName,
      c.source,
      c.victimSector,
      c.victimCountry,
      c.victimUrl,
      ...(c.relatedCves ?? []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
}

export type ClaimSort = 'newest' | 'oldest' | 'most-cves' | 'group-activity'

export const CLAIM_SORTS: ReadonlyArray<{ value: ClaimSort; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'most-cves', label: 'Most CVEs' },
  { value: 'group-activity', label: 'Group activity' },
]

function claimMs(c: DossierClaim): number | null {
  const ms = Date.parse(c.claimDate)
  return Number.isNaN(ms) ? null : ms
}

function compareClaimDate(a: DossierClaim, b: DossierClaim, direction: 'asc' | 'desc'): number {
  const aMs = claimMs(a)
  const bMs = claimMs(b)
  if (aMs === null && bMs === null) return 0
  if (aMs === null) return 1
  if (bMs === null) return -1
  return direction === 'asc' ? aMs - bMs : bMs - aMs
}

/**
 * Sort the loaded dossier rows. `group-activity` needs the recent-claim count
 * per group (from /groups) passed via `opts.recentByGroup`. Returns a new
 * array; never mutates the input.
 */
export function sortClaims<T extends DossierClaim>(
  claims: readonly T[],
  sort: ClaimSort,
  opts: { recentByGroup?: Map<string, number> } = {},
): T[] {
  const recent = opts.recentByGroup ?? new Map<string, number>()
  const byNewest = (a: T, b: T) => compareClaimDate(a, b, 'desc')
  const sorted = [...claims]
  switch (sort) {
    case 'oldest':
      sorted.sort((a, b) => compareClaimDate(a, b, 'asc'))
      break
    case 'most-cves':
      sorted.sort(
        (a, b) =>
          (b.relatedCves?.length ?? 0) - (a.relatedCves?.length ?? 0) ||
          byNewest(a, b),
      )
      break
    case 'group-activity':
      sorted.sort(
        (a, b) =>
          (recent.get(b.groupId) ?? 0) - (recent.get(a.groupId) ?? 0) ||
          byNewest(a, b),
      )
      break
    case 'newest':
    default:
      sorted.sort(byNewest)
      break
  }
  return sorted
}

export type ClaimChip = 'named' | 'with-cves' | 'with-source'

export const CLAIM_CHIPS: ReadonlyArray<{ value: ClaimChip; label: string }> = [
  { value: 'named', label: 'Named victim' },
  { value: 'with-cves', label: 'With CVEs' },
  { value: 'with-source', label: 'With source URL' },
]

const CHIP_PREDICATES: Record<ClaimChip, (c: DossierClaim) => boolean> = {
  named: (c) => Boolean(c.victimName && c.victimName.trim().length > 0),
  'with-cves': (c) => (c.relatedCves?.length ?? 0) > 0,
  'with-source': (c) => Boolean(c.victimUrl && c.victimUrl.trim().length > 0),
}

/**
 * Apply the active quick-filter chips with AND semantics. Only data-backed
 * chips exist (named victim / has CVEs / has source URL) — we deliberately do
 * not ship "watched"/"high-confidence"/"new groups" chips the contract can't
 * honestly support.
 */
export function applyChips<T extends DossierClaim>(
  claims: readonly T[],
  active: Record<string, boolean>,
): T[] {
  const predicates = (Object.keys(CHIP_PREDICATES) as ClaimChip[]).filter(
    (k) => active[k],
  )
  if (predicates.length === 0) return [...claims]
  return claims.filter((c) => predicates.every((k) => CHIP_PREDICATES[k](c)))
}
